import type { TCalculatePlanningRowsParams, TRowData } from 'App/irrigation-scheduler/utils/store/types'
import moment from 'moment-timezone'
import { unitConverter } from 'utils/unitConverter/unitConverter'
import { buildPlanningRowData } from './buildPlanningRowData'
import { computeDailyIrrigation } from './computeDailyIrrigation'

export const calculatePlanningRows = ({
  zoneEtcData,
  appliedEvents,
  scheduledEvents,
  scheduledPulseEvents,
  flowRateMmPerHour,
  timezone,
  viewStart,
  viewEnd,
  showUnitInLabel,
}: TCalculatePlanningRowsParams): TRowData[] => {
  const etcValues = zoneEtcData?.blockData?.etc || []
  const rainValues = zoneEtcData?.blockData?.rain || []

  const etcByDate = new Map(
    etcValues.map((etc) => [moment.tz(etc.timestamp, timezone).format('YYYY-MM-DD'), Number(etc.value)]),
  )

  const rainByDate = new Map(
    rainValues.map((rain) => [moment.tz(rain.timestamp, timezone).format('YYYY-MM-DD'), Number(rain.value)]),
  )

  const startDay = viewStart.clone().startOf('day')
  const numDays = viewEnd.diff(startDay, 'days')
  const days = Array.from({ length: numDays }, (_, i) => startDay.clone().add(i, 'days'))

  const dailyEtc = days.map((day) => {
    const dateKey = day.format('YYYY-MM-DD')
    const rawValue = etcByDate.get(dateKey)

    return rawValue !== undefined ? (unitConverter.evapotranspiration(rawValue).value() ?? 0) : null
  })

  const dailyRain = days.map((day) => {
    const dateKey = day.format('YYYY-MM-DD')
    const rawValue = rainByDate.get(dateKey)

    return rawValue !== undefined ? (unitConverter.precipitation(rawValue).value() ?? 0) : null
  })

  const dailyScheduled =
    scheduledEvents || scheduledPulseEvents
      ? computeDailyIrrigation(
          days,
          scheduledEvents ?? [],
          scheduledPulseEvents ?? [],
          flowRateMmPerHour,
        ).map(({ depthMm }) => unitConverter.irrigationActivity(depthMm, { inputUnit: 'mm' }).value() ?? 0)
      : undefined

  const dailyApplied = appliedEvents
    ? computeDailyIrrigation(days, appliedEvents, [], flowRateMmPerHour).map(
        ({ depthMm }) => unitConverter.irrigationActivity(depthMm, { inputUnit: 'mm' }).value() ?? 0,
      )
    : undefined

  const depthUnit = unitConverter.evapotranspiration().suffix()

  return buildPlanningRowData({
    dailyEtc,
    dailyRain,
    dailyScheduled,
    dailyApplied,
    depthUnit,
    showUnitInLabel,
  })
}
